import * as React from "react";
import Typography from "@mui/material/Typography";
import PaperCard from "./PaperCard";
import SelectOptions from "./SelectOptions";
import CloseIcon from "@mui/icons-material/Close";
import ModalPopup from "./ModalPopup";
import UploadFileInput from "./UploadFileInput";
import { useSelector } from "react-redux";
import PDFViewer from "./PDFViewer";
import {
  Grid,
  Box,
  styled,
  Divider,
  Skeleton,
  Modal,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { InputField } from "./Inputs";
import {
  IconButton,
  OutlinedPrimaryButton,
  OutlinedSmallButton,
  PrimaryButton,
  SecondaryButton,
  UploadButton,
} from "./Button";
import Toast from "./Toast";
import { RootState } from "../../services/redux/store";
import { setStatusPopup } from "../../services/redux/slices/statusPopupSlice";
import { useDispatch } from "react-redux";
import {
  authSelector,
  userLoanCatSelector,
} from "../..//services/redux/slices/authSlice";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import UploadIcon from "@mui/icons-material/Upload"; // Import upload icon
import AddIcon from "@mui/icons-material/Add";
import ImageViewer from "react-simple-image-viewer";
// ===================== Api's calling =======================
import documentsServices from "../../services/api/service/documentService";
import LoaderBackdrop from "./LoaderBackdrop";
import { resetDisbursementButtonValue } from "../../services/redux/slices/disbursementButtonSlice";
import {
  clearDocumentFormType,
  setDocumentNewData,
} from "../../services/redux/slices/documentFormSlice";
// ===========================================================

interface FormData {
  step1Input: string;
  step2Select: string;
}
interface Option {
  value: string;
  label: string;
}

interface FormValue {
  id: number; // Use `number` instead of `Number`
  documentFiles: File[];
  isOldValue: boolean;
  docId: number;
  docName?: string;
  otherDocName?: string;
  otherDoc?: boolean;
}
interface AddNewDocumentsProps {
  disbursementFormId: number;
  activeFunction: number;
  setActiveFunction: (value: number) => void;
  paymentTypeId: number;
  setActiveStep: (value: number) => void;
  remarkId: number;
  remark: string;
  isNewUniversity: any;
  accountTypeId: number;
  isMoveToNext: boolean;
  setIsLoadingUpdate: any;
  isLoadingUpdate: boolean;
  requestType?: any;
  setallDocsPresent: (value: boolean) => void;
}

/**
 * Component for adding new documents in the disbursement form.
 *
 * @component
 * @param {AddNewDocumentsProps} props - The props for the component.
 * @param {string} props.disbursementFormId - The ID of the disbursement form.
 * @param {number} props.activeFunction - The active function identifier.
 * @param {Function} props.setActiveFunction - Function to set the active function.
 * @param {string} props.paymentTypeId - The ID of the payment type.
 *
 * @returns {JSX.Element} The rendered component.
 *
 * @example
 * <AddNewDocuments
 *   disbursementFormId="12345"
 *   activeFunction={0}
 *   setActiveFunction={setActiveFunction}
 *   paymentTypeId="67890"
 * />
 *
 * @remarks
 * This component handles the addition, deletion, and preview of documents.
 * It also manages the state of the form and interacts with the document services API.
 *
 * @function handleNext - Advances the active step.
 * @function handleBack - Moves back the active step.
 * @function handleToastClose - Closes the toast notification.
 * @function handleLoanTypeId - Memoized function to get the loan type ID.
 * @function handleAddNewField - Adds a new document field.
 * @function handleDeleteField - Deletes a document field.
 * @function handleGetDocsFileList - Fetches the list of document files for a given document ID.
 * @function handleGetAllDocumentFileList - Fetches the list of all document files.
 * @function handleGetDocsList - Fetches the list of documents.
 * @function handleGetListForNewFields - Fetches the list of new documents.
 * @function convertToBase64 - Converts a file to base64 format.
 * @function handleFileChange - Handles file change events.
 * @function handleDeleteFile - Deletes a file.
 * @function handleCloseModal - Closes the modal and fetches document files.
 * @function handleSelectedNewFieldValue - Handles the selection of a new field value.
 * @function handleSaveAllDocument - Saves all documents.
 * @function handleDeleteAllDocument - Deletes all documents.
 * @function handleContinousSave - Placeholder for continuous save functionality.
 * @function handleShowImageMList - Memoized function to render the list of images and documents.
 * @function renderStepContent - Renders the content for the current step.
 *
 * @hook useEffect - Fetches the document list on component mount.
 * @hook useEffect - Updates the form value when the file list or non-mandatory list changes.
 * @hook useEffect - Handles active function changes.
 */
const AddNewDocuments: React.FC<AddNewDocumentsProps> = ({
  disbursementFormId,
  activeFunction,
  setActiveFunction,
  paymentTypeId,
  setActiveStep,
  remarkId,
  remark,
  isNewUniversity,
  accountTypeId,
  isMoveToNext,
  setIsLoadingUpdate,
  isLoadingUpdate,
  requestType,
  setallDocsPresent,
}) => {
  // ========================= data list and detail ===========================
  // ==========================================================================
  const dispatch = useDispatch();

  // ========================= modal state =============================
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [isUploadModal, setIsUploadModal] = React.useState(false);
  const [isAddNewDocModal, setIsAddNewDocModal] = React.useState(false);
  const theme = useTheme();
  const isXsOrSm = useMediaQuery(theme.breakpoints.down("sm"));
  const [isDocumentData, setIsDocumentData] = React.useState(false);
  // ==================================================================

  const [newDocumentValue, setNewDocumentValue] = React.useState(0);
  const [newDocumentValueExtra, setNewDocumentValueExtra] = React.useState(0);
  const [otherDocumentName, setOtherDocumentName] = React.useState(""); // Track "Other" document name input

  const [documentId, setDocumentId] = React.useState(0);
  const [documentIndex, setDocumentIndex] = React.useState(0);

  const [docIndex, setDocIndex] = React.useState(0);
  const [formData, setFormData] = React.useState<FormData>({
    step1Input: "",
    step2Select: "",
  });
  const [formValue, setFormValue] = React.useState<FormValue[]>([]);
  const [nonMandatoryList, setNonMandatoryList] = React.useState<Array<{label: string, value: any, dmsCode: any}>>([]);
  const [documentListValue, setDoumentListValues] = React.useState([]);
  const [fileList, setFileList] = React.useState([]);

  const [isLoading, setIsLoading] = React.useState(true);
  const [isUploadLoading, setIsUploadLoading] = React.useState(false);

  const [toastOpen, setToastOpen] = React.useState(false);
  const [severity, setSeverity] = React.useState<
    "error" | "warning" | "info" | "success"
  >("error");
  const [message, setMessage] = React.useState("");
  const selectedLoanNumber = useSelector(
    (state: RootState) => state?.dashboardData?.selectedLoanNumber
  );
  const loanData = useSelector((state: RootState) => state?.dashboardData);
  const selectedFormType = useSelector(
    (state: RootState) => state?.documentFormSlice
  );
  const [selectedDocuments, setSelectedDocuments] = React.useState<string[]>(
    []
  );

  const { businessType } = useSelector(authSelector);
  const userLoanCategory = useSelector(userLoanCatSelector);

  const handleToastClose = () => {
    setToastOpen(false);
  };

  React.useEffect(() => {
    handleGetDocsList(0);
  }, [isNewUniversity]);

  React.useEffect(() => {
    const updatedFormValue: any = [...formValue]; // Create a shallow copy of formValue

    // Step 1: Remove documentFiles that no longer exist in fileList
    updatedFormValue.forEach((itemA: any) => {
      itemA.documentFiles = itemA?.documentFiles?.filter((docFile: any) =>
        fileList?.some((fileItem: any) => fileItem.id === docFile.id)
      );
    });

    // Step 2: If fileList is empty, remove entries in formValue with no documentFiles and isOldValue set to false
    if (fileList.length === 0) {
      for (let i = updatedFormValue.length - 1; i >= 0; i--) {
        if (
          !updatedFormValue[i].isOldValue &&
          updatedFormValue[i].documentFiles.length === 0
        ) {
          updatedFormValue.splice(i, 1);
        }
      }
    }

    // Step 3: Add any new documentFiles from fileList that are missing in formValue
    updatedFormValue.forEach((itemA: any) => {
      const matchingDocs = fileList?.filter(
        (itemB: any) => itemB.docId === itemA.docId
      );

      matchingDocs.forEach((matchingDoc: any) => {
        const isAlreadyPresent = itemA?.documentFiles?.some(
          (docFile: any) => docFile.id === matchingDoc.id
        );
        if (!isAlreadyPresent) {
          itemA?.documentFiles?.push(matchingDoc);
          itemA["uploadedBy"] = matchingDoc?.uploadedBy;
        }
      });
    });

    // Step 4: Loop through nonMandatoryList and add matching entries to formValue if they don't already exist
    nonMandatoryList.forEach((nonMandatoryItem: any) => {
      const matchingFiles: any = fileList?.filter(
        (fileItem: any) => fileItem.docId === nonMandatoryItem.value
      );

      if (matchingFiles.length > 0) {
        const existingEntry: any = updatedFormValue?.find(
          (oldFileItem: any) => oldFileItem.docId == nonMandatoryItem.value
        );
        if (!existingEntry) {
          const newEntry: any = {
            id: updatedFormValue.length + 1,
            documentFiles: [...matchingFiles],
            isOldValue: false,
            docId: nonMandatoryItem.value,
            docName: nonMandatoryItem.label,
            docDms: nonMandatoryItem.dmsCode,
            docOtherName: matchingFiles?.[0]?.docOtherName,
            uploadedBy: matchingFiles?.[0]?.uploadedBy,
          };
          updatedFormValue.push(newEntry);
        } else {
          matchingFiles.forEach((matchingFile: any) => {
            const isFileAlreadyAdded = existingEntry?.documentFiles?.some(
              (docFile: any) => docFile.id === matchingFile.id
            );
            if (!isFileAlreadyAdded) {
              existingEntry.documentFiles.push(matchingFile);
            }
          });
        }
      }
    });
    // Step 6: Update the state with the modified formValue
    if(updatedFormValue?.length>0 ){
      setFormValue(updatedFormValue);
    }
  }, [fileList]);
  React.useEffect(() => {
    if (activeFunction == 1) {
      handleDeletFilesModal();
    } else if (activeFunction == 2) {
      handleSaveAllDocument("NAVIGATE");
    }
  }, [activeFunction]);
  React.useEffect(() => {
    // Only set specific documents for paymentTypeId 5 when nonMandatoryList is populated
    // and formValue is empty or doesn't contain the required documents
    if (paymentTypeId === 5 && nonMandatoryList?.length > 0) {
      const valueName = ["Visa Copy", "Forex Remittance 1", "Demand Letter"];
      let filterData: any[] = nonMandatoryList.filter((elem: any) =>
        valueName.includes(elem?.label)
      );
      if (filterData.length > 0) {
        const transformedData = filterData.map((item: any, index: number) => {
          return {
            docDms: item.dmsCode,
            docId: item.value,
            docName: item.label,
            otherDoc: false,
            otherDocName: "",
            docOtherName: "",
            documentFiles: [],
            id: index + 1,
            isOldValue: true,
            isCheckNon:true,
          };
        });
        setFormValue(transformedData);
      }
    }
  }, [nonMandatoryList, paymentTypeId]);
  /**
   * Adds a new field to the form value state.
   *
   * This function creates a copy of the current form value state, appends a new field object to it,
   * and updates the state with the new array. Each new field object contains a unique `id`, an empty
   * array for `documentFiles`, a boolean `isOldValue` set to `false`, and a `docId` initialized to `0`.
   */
  /**
   * Handles the deletion of a field from the form values.
   *
   * @param indexValue - The index of the field to be deleted.
   *
   * The function first finds the value at the specified index in the form values.
   * It then filters out the value at the specified index to create an updated form values array.
   *
   * If the document files associated with the found value are empty, it updates the form values state.
   * Otherwise, it calls the `handleDeleteFile` function to delete the file.
   */
  const handleDeleteField = (indexValue: number, deleteType: string) => {
    const findValue: any =
      formValue?.[documentIndex]?.documentFiles?.[indexValue];
    if (deleteType == "SINGLE") {
      handleDeleteFile(findValue?.id, indexValue, 1);
    }

    if (deleteType == "ALL") {
      handleDeleteFile(0, indexValue, 2);
    }

  };

  // ======================================== API INVOKING FUNCTIONS ================================================

  /**
   * Fetches the list of document files for a given document ID and updates the document list state.
   *
   * @param doc_id - The ID of the document to fetch the file list for.
   * @returns A promise that resolves when the document file list has been fetched and the state has been updated.
   */
  const handleGetDocsFileList = async (doc_id: any) => {
    try {
      const body = {
        disbursementFormId: disbursementFormId,
        documentId: doc_id,
      };
      const response = await documentsServices.getDocumentFileList(body);
      if (response.status == 200) {
        setDoumentListValues(response?.data?.data);
      }
    } catch (error) {
      console.log(error);
    }
  };

  /**
   * Fetches the list of all document files associated with a specific disbursement form.
   *
   * This function sets the loading state to `true` before making an asynchronous call to
   * `documentsServices.getAllDocumentFileList` with the provided `disbursementFormId`.
   * If the response status is 200, it updates the file list state with the received data
   * and sets the loading state to `false`. In case of an error, it logs the error and
   * sets the loading state to `false`.
   *
   * @async
   * @function handleGetAllDocumentFileList
   * @returns {Promise<void>} A promise that resolves when the document file list is fetched and state is updated.
   */
  const handleGetAllDocumentFileList = async () => {
    try {
      const response = await documentsServices.getAllDocumentFileList(
        disbursementFormId
      );
      if (response.status == 200) {
        setIsUploadLoading(false);
        setIsLoadingUpdate(false);
        setIsLoading(false);
        if (response?.data?.data.length <= 0) {
          dispatch(setDocumentNewData(false));
        }
        const findValue = response?.data?.data?.filter(
          (elem: any) => elem?.docId == documentId
        );

        if (findValue.length > 0) {
          setFileList(response?.data?.data);
        } else {
          setFileList(response?.data?.data);
          setIsDocumentData(false);
          setIsUploadModal(false);
        }
      }
    } catch (error) {
      setIsLoadingUpdate(false);
      setIsLoading(false);
      console.log(error);
    }
  };

  const delay = (ms: number) =>
    new Promise((resolve) => setTimeout(resolve, ms));

  /**
   * Handles the retrieval of the documents list.
   *
   * This function sets the loading state to true, then attempts to fetch the document list
   * from the `documentsServices` based on the `paymentTypeId`. If the response is successful,
   * it processes each document item with a delay and constructs an array of document objects.
   * The constructed array is then set to the form value and used to get additional fields.
   *
   * @async
   * @function handleGetDocsList
   * @returns {Promise<void>} A promise that resolves when the document list has been processed and set.
   * @throws Will log an error and set loading state to false if the request fails.
   */
  const handleGetDocsList = async (apiCallNumber: number): Promise<void> => {
    setIsLoadingUpdate(true);
    try {
      const arrayOfObj: any = [];

      const loan_type_id = loanData?.sanctionedDisbursedData.find(
        (elem: any) => elem?.lanNumber == selectedLoanNumber
      )?.loanTypeId;

      const response: any = await documentsServices.getDocumentList(
        paymentTypeId,
        loan_type_id,
        isNewUniversity
      );
      if (response.status === 200) {
        setIsLoading(false);
        // setIsUploadLoading(false)
        const filterData: any = response?.data;
        if (paymentTypeId == 1 && accountTypeId != 2) {
          const findIndexValue: any = filterData.findIndex(
            (elem: any) => elem?.docType?.code == 1218
          );
          filterData.splice(findIndexValue, 1);
        }
        await Promise.all(
          filterData.map(async (item: any, index: number) => {
            await delay(index * 300); // Adjust the delay (300ms here) as needed

            const jsonObject = {
              docId: item?.docType?.id,
              docName: item?.docType?.name,
              docDms: item?.docType?.code,
              documentFiles: [],
              isOldValue: false,
              documentUploadCount: item?.document_upload_count,
            };

            jsonObject["isOldValue"] = true;
            arrayOfObj.push(jsonObject);
          })
        );
        
        if (apiCallNumber == 0) {
          setFormValue(arrayOfObj);
        }
        handleGetListForNewFields(arrayOfObj);
       
       
        // setIsLoadingUpdate(false);
      }
    } catch (error) {
      setIsLoading(false);
      console.log(error);
    }
  };

  /**
   * Fetches a new list of documents from the document service, filters out documents
   * that are already present in the provided array of objects, and updates the non-mandatory
   * document list state with the filtered results. Also triggers the retrieval of all document files.
   *
   * @param arrayOfObj - An array of objects containing document information, each object should have a `docId` property.
   * @returns {Promise<void>} - A promise that resolves when the operation is complete.
   *
   * @throws Will log an error to the console and set the loading state to false if the request fails.
   */
  const handleGetListForNewFields = async (arrayOfObj: any) => {
    try {
      const response: any = await documentsServices.getNewDocumentList();
      if (response?.status == 200) {
        const docIds = new Set(arrayOfObj.map((item: any) => item?.docId));
        const filteredSecondArray = response?.data
          .filter((item: any) => !docIds.has(item.id))
          .map((elem: any) => {
            return {
              label: elem?.name,
              value: elem?.id,
              dmsCode: elem?.code,
            };
          });
        setIsUploadLoading(false);
        setIsLoading(false);

        // filteredSecondArray.push({
        //   label: "Other",
        //   value: "Other",
        //   dmsCode: 0,
        // });
        setNonMandatoryList(filteredSecondArray);
        handleGetAllDocumentFileList();
      }
    } catch (error) {
      console.log(error);
      setIsLoading(false);
    }
  };

  /**
   * Converts a given file to a base64 encoded string.
   *
   * @param file - The file to be converted.
   * @returns A promise that resolves with an object containing the file name, base64 encoded data, and file format.
   *
   * The function performs the following checks and operations:
   * - Ensures the file size does not exceed 10MB.
   * - Validates the file format to be one of the allowed formats: PDF, PNG, JPEG, JPG.
   * - Converts the file to a base64 encoded string.
   *
   * If the file size exceeds the limit or the format is invalid, the function will reject the promise with an appropriate error message.
   *
   * @throws Will throw an error if the file size exceeds 10MB or if the file format is not allowed.
   */
  const convertToBase64 = (
    file: File
  ): Promise<{ name: string; base64Data: string; format: string }> => {
    return new Promise((resolve, reject) => {
      const maxFileSize = 10 * 1024 * 1024; // 10MB limit
      const allowedFormats = [
        "pdf",
        "png",
        "jpeg",
        "jpg",
        "PNG",
        "JPEG",
        "JPG",
      ];

      // Check file size
      if (file.size > maxFileSize) {
        dispatch(
          setStatusPopup({
            status: "error",
            message: `File size exceeds the 10MB limit`,
          })
        );
        return reject(new Error("File size exceeds the 10MB limit"));
      }

      // Extract file extension and check if it's allowed
      const fileExtension = file.name.split(".").pop()?.toLocaleLowerCase();
      if (!fileExtension || !allowedFormats.includes(fileExtension)) {
        dispatch(
          setStatusPopup({
            status: "error",
            message: `Invalid file format. Only PDF, PNG, JPEG, and JPG are allowed.`,
          })
        );
        return reject(new Error("Invalid file format"));
      }

      // Convert file to base64
      const reader: FileReader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64Data = reader.result as string;
        const plainBase64Data = base64Data.replace(/^data:(.*?);base64,/, ""); // Removes the prefix
        resolve({
          name: file.name,
          base64Data: plainBase64Data, // Return the clean base64 without the data URL prefix
          format: fileExtension, // Return the file extension
        });
      };
      reader.onerror = (error: any) => reject(error);
    });
  };

  /**
   * Handles the file change event, converts the selected files to base64, and updates the form values accordingly.
   * If the document has a valid ID, it uploads the files to the server.
   *
   * @param id - The ID of the document.
   * @param files - The list of files selected by the user.
   * @param index - The index of the document in the form values.
   *
   * @returns {Promise<void>} - A promise that resolves when the file handling is complete.
   *
   * @throws Will throw an error if there is an issue converting files to base64.
   */

  const handleFileChange = async (
    id: number,
    files: FileList | null,
    index: number
  ) => {
    setIsAddNewDocModal(false);

    let fileArray: any;

    if (!files) return;

    let base64FilesPromises: any = Array.from(files).map((file) =>
      convertToBase64(file)
    );
    try {
      // Wait until all files are converted to base64
      const base64Files = await Promise.all(base64FilesPromises);
      const fieldsValue: any = formValue[index];

      if (
        !fieldsValue?.documentUploadCount ||
        fieldsValue?.documentFiles?.length < fieldsValue?.documentUploadCount
      ) {
        setIsUploadLoading(true);
        const updatedFields: any = formValue.map((field: any) => {
          fileArray = base64Files;
          return field;
        });
        const findValue: any = updatedFields.find(
          (_: any, indexValue: number) => indexValue == index
        );

        if (findValue?.docId != 0) {
          setIsLoading(true);
          const bodyObj = {
            disbursementFormId: disbursementFormId,
            documentId: findValue?.docId,
            docTitle: findValue?.docName,
            dmsCode: String(findValue?.docDms),
            documentFiles: [],
            otherDoc: findValue?.otherDoc,
            docName: findValue?.otherDocName,
          };

          bodyObj.documentFiles = fileArray.map((elem: any) => {
            return { base64Data: elem?.base64Data, format: elem?.format };
          });

          const response: any = await documentsServices.postDocumentUpload(
            bodyObj
          );
          if (response?.status == 200) {
            dispatch(setDocumentNewData(true));
            handleGetDocsList(1);
            setIsDocumentData(true);
            setIsAddNewDocModal(false);
            // setIsUploadLoading(false);
            setNewDocumentValue(0);
            setSelectedDocuments([]);
          }
        } else {
          dispatch(
            setStatusPopup({
              status: "error",
              message: "Please select document!",
            })
          );
          return;
        }
      } else {
        setIsUploadLoading(false);
        setIsLoading(false);
        dispatch(
          setStatusPopup({
            status: "error",
            message: `You cannot upload file more than the ${fieldsValue?.documentUploadCount}`,
          })
        );
      }
    } catch (error: any) {
      // axios rejects non-2xx responses, so a failed upload lands here rather than in the
      // `else` branch above — stop the loaders and show the API's message.
      console.error("Error uploading document:", error);
      setIsUploadLoading(false);
      setIsLoading(false);
      dispatch(
        setStatusPopup({
          status: "error",
          message: error?.response?.data?.message ?? "Please select valid document!",
        })
      );
    }
  };

  const handleDeleteSingleFileModal = (
    fieldId: number,
    fileIndex: number,
    type: number,
    deleteType: string
  ) => {
    // dispatch(
    //   setStatusPopup({
    //     status: "info",
    //     message: "Confirm to delete file !",
    //     isFunction: true,
    //     locationFunction: () => handleDeleteFile(fieldId, fileIndex, type),
    //     hasLocationFunction: true,
    //   })
    // );
    handleDeleteField(fileIndex, deleteType);
  };

  /**
   * Handles the deletion of a file from the form value and updates the document list.
   *
   * @param {number} fieldId - The ID of the field to delete.
   * @param {number} fileIndex - The index of the file in the form value array.
   * @param {number} type - The type of the document.
   * @returns {Promise<void>} - A promise that resolves when the file deletion is complete.
   *
   * The function performs the following steps:
   * 1. Finds the value in the form value array based on the file index.
   * 2. Constructs a delete object with necessary IDs and type.
   * 3. Calls the deleteDocumentFile service to delete the file.
   * 4. If the deletion is successful, updates the UI with a success message and refreshes the document list.
   * 5. If the deletion is unsuccessful, logs the error and stops the loading state.
   */
  const handleDeleteFile = async (
    fieldId: number,
    fileIndex: number,
    type: number
  ): Promise<void> => {
    setIsUploadLoading(true);
    setIsLoading(true);

    const findValue: any = formValue.find(
      (_: any, indexValue: number) => indexValue == fileIndex
    );

    const deleteObj: any = {
      disbursementFormId: disbursementFormId,
      documentId: findValue?.docId,
      type: type,
    };

    if (fieldId != 0) {
      deleteObj["disbursemntDocId"] = fieldId;
    } else {
      deleteObj["disbursemntDocId"] = 0;
    }
    try {
      const response: any = await documentsServices.deleteDocumentFile(
        deleteObj
      );
      if (response?.status == 200) {
        setIsModalOpen(false);
        setIsLoading(false);
        // dispatch(
        //   setStatusPopup({
        //     status: "success",
        //     message: response?.data?.message,
        //   })
        // );
        if (fieldId == 0) {
          const updateFormValue: any = formValue.filter(
            (_: any, index: number) => index != fileIndex
          );
          setFormValue(updateFormValue);
          // handleGetDocsFileList(findValue?.docId);
          handleGetDocsList(0);
        } else {
          if (isModalOpen) {
            handleGetDocsFileList(findValue?.docId);
            handleGetDocsList(0);
          } else {
            handleGetDocsList(0);
          }
        }
      }
    } catch (error: any) {
      setIsLoading(false);
      console.log(error);
    }
  };

  /**
   * Handles the closing of the modal by setting the document index,
   * fetching the document file list, and opening the modal.
   *
   * @param {number} index - The index of the document.
   * @param {any} docId - The ID of the document.
   */
  const handleCloseModal = (index: number, docId: any) => {
    if (docId == 0) {
      dispatch(
        setStatusPopup({
          status: "success",
          message: "Please select document to view files",
        })
      );
    } else {
      setDocIndex(index);
      handleGetDocsFileList(docId);
      setIsModalOpen(true);
    }
  };

  /**
   * Handles the selection of a new field value and updates the form value accordingly.
   *
   * @param value - The selected value.
   * @param index - The index of the form value to update.
   *
   * This function creates a copy of the current form values, finds the corresponding
   * data from the non-mandatory list based on the selected value, and updates the
   * form value at the specified index with the new document ID, DMS code, and label.
   * Finally, it sets the updated form values.
   */
  /**
   * Handles the selection of a new field value.
   *
   * @param {any} value - The selected value.
   * @param {number} index - The index of the form value to update.
   *
   * This function updates the form value and selected documents based on the selected value.
   * It checks if the selected value's label contains "other" (case-insensitive) and updates the form value accordingly.
   * If the label contains "other", it sets the `otherDoc` property to true and clears the `otherDocName`.
   * Otherwise, it sets the `otherDoc` property to false and clears both `otherDocName` and `docOtherName`.
   * It also updates the selected documents list by adding or removing the selected value.
   */
  const handleSelectedNewFieldValue = (value: any) => {
    const copyFormValue: any = [...formValue];
    const copySelectedDocuments = [...selectedDocuments];
    
    // First, remove any previously selected document that doesn't have files and is not an old value
    if (newDocumentValue && newDocumentValue !== 0) {
      // Remove previous selection from formValue if it doesn't have files
      const previousIndex = copyFormValue.findIndex(
        (elem: any) => elem?.docId == newDocumentValue && !elem?.isOldValue && elem?.documentFiles?.length === 0
      );
      if (previousIndex !== -1) {
        copyFormValue.splice(previousIndex, 1);
      }
      
      // Remove previous selection from selectedDocuments
      const previousSelectedIndex = copySelectedDocuments.indexOf(String(newDocumentValue));
      if (previousSelectedIndex !== -1) {
        copySelectedDocuments.splice(previousSelectedIndex, 1);
      }
    }

    const findValue: any = nonMandatoryList.find(
      (elem: any) => elem?.value == value
    );
    const labelContainsOther: boolean = findValue?.label
      ? /other/i.test(findValue.label) // Case-insensitive check
      : false;
    const fileterData: any = nonMandatoryList.find(
      (elem: any) => elem?.value == value
    );
    const jsonData: any = {
      docDms: fileterData?.dmsCode,
      docId: value,
      docName: fileterData?.label,
      otherDoc: labelContainsOther ? true : false,
      otherDocName: labelContainsOther ? "" : "",
      docOtherName: labelContainsOther ? undefined : "",
      documentFiles: [],
    };

    const findValueById: any = copyFormValue.filter(
      (elem: any) => elem?.docId == value
    );

    if (copySelectedDocuments.includes(String(value)) || findValueById.length > 0) {
      dispatch(
        setStatusPopup({
          status: "warning",
          message:
            "You have already selected this document, Please select other one.",
        })
      );
    } else {
      copyFormValue.push(jsonData);
      copySelectedDocuments.push(String(value));
      setSelectedDocuments(copySelectedDocuments);
      setFormValue(copyFormValue);
      setNewDocumentValue(value);
      setNewDocumentValueExtra(value);
      setOtherDocumentName(""); // Reset other document name when new document is selected
    }
  };

  /**
   * Handles the saving of all documents.
   *
   * This function performs the following steps:
   * 1. Checks if there are any documents in the form value. If not, it sets an error message and returns.
   * 2. Validates that every document has files associated with it.
   * 3. If validation passes, it sends a request to save all documents.
   * 4. Based on the response, it sets the appropriate state and displays a success or error message.
   *
   * @async
   * @function handleSaveAllDocument
   * @returns {Promise<void>} A promise that resolves when the save operation is complete.
   */
  const handleSaveAllDocument = async (type: string): Promise<void> => {
    setIsLoadingUpdate(true);
    let isValid = formValue.every((item) => item.documentFiles.length > 0);
    if(paymentTypeId===5){
      isValid=true;
    }
    if (type == "NAVIGATE" && !isValid) {
      dispatch(
        setStatusPopup({
          status: "error",
          message: "Please upload all required documents.",
        })
      );
      setActiveFunction(0)
      setIsLoadingUpdate(false);
      return;
    } else {
      setActiveFunction(0);
      const body = {
        disbursementFormId: disbursementFormId,
        remark: remark,
        remarkId: remarkId,
      };
      if (isMoveToNext === true) {
        setallDocsPresent(true);
        if (requestType === "lien") {
          setActiveStep(2);
        }
        setIsLoadingUpdate(false);
      }
      if (isMoveToNext === false) {
        const response: any = await documentsServices.saveAllDocument(body);
        if (response?.status == 200) {
          setIsLoadingUpdate(false);
          dispatch(clearDocumentFormType());
          setallDocsPresent(true);
          setActiveFunction(0);
          if (type == "NAVIGATE") {
            if(requestType==="lien"){
               setActiveStep(2);
            }else{
              setActiveStep(3);
            }
          }
        }
      }
    }
  };

  /**
   * Handles the deletion of all documents associated with a disbursement form.
   *
   * This function sets the loading state to true, constructs a request body with the
   * disbursement form ID, and sends a request to delete all documents. If the response
   * status is 200, it resets the loading state and updates the active function and step.
   *
   * @async
   * @function handleDeleteAllDocument
   * @returns {Promise<void>} A promise that resolves when the operation is complete.
   */

  const handleDeletFilesModal = () => {
    if (selectedFormType?.isNewData) {
      setActiveFunction(0);
      dispatch(
        setStatusPopup({
          status: "info",
          message:
            "Unsaved data will be lost. Please save your changes before leaving this page.",
          isFunction: true,
          locationFunction: () => handleDeleteAllDocument(),
        })
      );
    } else {
      dispatch(clearDocumentFormType());
      setActiveStep(0);
      setActiveFunction(0);
    }
  };

  const handleDeleteAllDocument = async () => {
    setIsLoading(true);
    // const documentIds = formValue.map((item: any) => item.docId);
    const body = {
      disbursementFormId: disbursementFormId,
    };
    const response: any = await documentsServices.deleteAllDocument(body);
    if (response?.status == 200) {
      dispatch(clearDocumentFormType());
      setIsLoading(false);
      setActiveFunction(0);
      setActiveStep(0);
    }
  };

  // ===========================================================================================================
  const handleShowImageMList = () => {
    const viewData: any = documentListValue?.[docIndex];
    const uploadBy: any = formValue.find((elem: any) => elem?.docId == viewData?.docId)
    return (
      <>
        <div>
          <Grid container spacing={2} sx={{ paddingBottom: "20px" }}>
            <Grid item xs={12}>
              <div
                className="image-container"
                style={{
                  border: "1px solid #ddd",
                  padding: "10px",
                  minWidth: isXsOrSm ? "300px" : "100%",
                  height: isXsOrSm ? "320px" : "550px",
                  overflow: "auto",
                  marginTop: "39px",
                }}
              >
                {viewData?.docName?.includes("pdf") ? (
                  <PDFViewer pdfUrl={viewData?.docName} />
                ) : (
                  <img src={viewData?.docName} alt="sample" />
                )}
                {uploadBy?.uploadedBy == "customer" && isMoveToNext === false && (
                  <span className="close-icon">
                    <DeleteOutlineOutlinedIcon
                      sx={{
                        color: "#EA5455",
                        fontSize: "20px",
                        paddingTop: "1px",
                        backgroundColor: "none",
                      }}
                      onClick={() =>
                        handleDeleteSingleFileModal(
                          viewData?.id,
                          docIndex,
                          1,
                          "SINGLE"
                        )
                      }
                    />
                  </span>
                )}
              </div>
            </Grid>
          </Grid>
        </div>
      </>
    );
  };

  const handleOpenUploadDocument = (id: any, index: number) => {
    const checkDocument: any = formValue.find((elem: any) => elem.docId == id);
    setIsDocumentData(checkDocument?.documentFiles?.length > 0);
    setDocumentId(id);
    setDocumentIndex(index);
    setIsUploadModal(true);
  };

  const handleShowUploadCard = React.useMemo(() => {
    // return (
      <>
        <Divider sx={{ paddingTop: "20px" }} />
        <div className="padding-box">
          <Grid container item gap={1}>
            {formValue.some((item: any) => item.documentFiles.length > 0) && (
              <Grid item sm={12}>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: "600", fontSize: "13px", color: "#00B3B5" }}
                >
                  UPLOADED
                </Typography>
              </Grid>
            )}
          </Grid>
          <Grid container item spacing={2} sx={{ paddingTop: "10px" }}>
            {isLoading || isLoadingUpdate ? (
              <>
                {Array.from({ length: 4 }).map((_, index) => (
                  <Grid item xs={12} sm={12} md={6} key={index}>
                    <Box
                      sx={{
                        border: "1px solid #A8AAAE",
                        height: "60px",
                        borderRadius: "12px",
                        padding: "10px 16px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Box>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: "600",
                            fontSize: "15px",
                            color: "rgba(75, 70, 92, 0.9)",
                          }}
                        >
                          <Skeleton width={150} />
                        </Typography>
                        <Typography
                          sx={{
                            fontWeight: "400",
                            fontSize: "10px",
                            color: "rgba(75, 70, 92, 0.8)",
                          }}
                        >
                          <Skeleton width={100} />
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <IconButton>
                          <Skeleton variant="circular" width={24} height={24} />
                        </IconButton>
                        <IconButton>
                          <Skeleton variant="circular" width={24} height={24} />
                        </IconButton>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </>
            ) : (
              <>
                {formValue.map((item: any, index: any) => (
                  <>
                    {item?.documentFiles?.length > 0 && (
                      <Grid item xs={12} sm={12} md={6}>
                        <Box
                          sx={{
                            border: "1px solid #A8AAAE",
                            height: "60px",
                            borderRadius: "12px",
                            padding: "10px 16px",
                            display: "flex", // Use flex for alignment
                            alignItems: "center", // Align items vertically center
                            justifyContent: "space-between", // Add spacing between elements
                          }}
                        >
                          <Box>
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: "600",
                                fontSize: isXsOrSm ? "12px" : "15px",
                                color: "rgba(75, 70, 92, 0.9)",
                              }}
                            >
                              {item?.docName}
                              {item?.isOldValue && item?.isCheckNon!=true &&(
                                <>
                                  {" "}
                                  <span style={{ color: "red" }}>*</span>{" "}
                                </>
                              )}
                              {item?.docOtherName
                                ? `, ${item?.docOtherName}`
                                : item?.otherDocName
                                  ? `, ${item?.otherDocName}`
                                  : ""}
                            </Typography>
                            <Typography
                              sx={{
                                fontWeight: "400",
                                fontSize: "10px",
                                color: "rgba(75, 70, 92, 0.8)",
                              }}
                            >
                              Upto 10 mb
                            </Typography>
                          </Box>
                          <Box gap={5} sx={{ paddingTop: "5px",minWidth:"82px" }}>
                            {isXsOrSm ? (
                              <>
                                <VisibilityOutlinedIcon
                                  sx={{ color: "rgba(0, 179, 181, 0.8)" }}
                                  onClick={() =>
                                    handleOpenUploadDocument(item?.docId, index)
                                  }
                                />
                              </>
                            ) : (
                              <>
                                <IconButton
                                  onClick={() =>
                                    handleOpenUploadDocument(item?.docId, index)
                                  }
                                >
                                  {" "}
                                  <VisibilityOutlinedIcon
                                    sx={{ color: "rgba(0, 179, 181, 0.8)" }}
                                  />{" "}
                                </IconButton>
                              </>
                            )}

                            {isMoveToNext === false && (
                              <>
                                {item?.uploadedBy ==
                                  "customer" &&
                                  (
                                    <>
                                      {isXsOrSm ? (
                                        <>
                                          {" "}
                                          <DeleteOutlineOutlinedIcon
                                            sx={{ color: "#EA5455" }}
                                            onClick={() =>
                                              handleDeleteField(index, "ALL")
                                            }
                                          />{" "}
                                        </>
                                      ) : (
                                        <>
                                          <IconButton
                                            isTransparent={true}
                                            onClick={() =>
                                              handleDeleteField(index, "ALL")
                                            }
                                          >
                                            {" "}
                                            <DeleteOutlineOutlinedIcon
                                              sx={{ color: "#EA5455" }}
                                            />{" "}
                                          </IconButton>
                                        </>
                                      )}
                                    </>
                                  )}
                              </>
                            )}
                          </Box>
                        </Box>
                      </Grid>
                    )}
                  </>
                ))}
              </>
            )}
          </Grid>
        </div>

        <div className="padding-box" style={{ paddingTop: "0px" }}>
          <Grid container item sx={{ paddingTop: "20px" }}>
            {formValue.some((item: any) => item.documentFiles.length <= 0) && (
              <Grid item xs={12} sm={12} md={6}>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: "600", fontSize: "13px", color: "#00B3B5" }}
                >
                  TO BE UPLOADED
                </Typography>
              </Grid>
            )}
          </Grid>
          <Grid container item spacing={2} sx={{ paddingTop: "10px" }}>
            {isLoading || isLoadingUpdate ? (
              <>
                {Array.from({ length: 4 }).map((_, index) => (
                  <Grid item xs={12} sm={12} md={6} key={index}>
                    <Box
                      sx={{
                        border: "1px solid #A8AAAE",
                        height: "60px",
                        borderRadius: "12px",
                        padding: "10px 16px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Box>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: "600",
                            fontSize: "15px",
                            color: "rgba(75, 70, 92, 0.9)",
                          }}
                        >
                          <Skeleton width={150} />
                        </Typography>
                        <Typography
                          sx={{
                            fontWeight: "400",
                            fontSize: "10px",
                            color: "rgba(75, 70, 92, 0.8)",
                          }}
                        >
                          <Skeleton width={100} />
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <IconButton>
                          <Skeleton variant="circular" width={24} height={24} />
                        </IconButton>
                        <IconButton>
                          <Skeleton variant="circular" width={24} height={24} />
                        </IconButton>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </>
            ) : (
              <>
                {formValue.map((item: any, index: number) => (
                  <>
                    {item?.documentFiles?.length == 0 && (
                      <Grid item xs={12} sm={12} md={6}>
                        <Box
                          sx={{
                            border: "1px solid #A8AAAE",
                            height: "60px",
                            borderRadius: "12px",
                            padding: "10px 16px",
                            display: "flex", // Use flex for alignment
                            alignItems: "center", // Align items vertically center
                            justifyContent: "space-between", // Add spacing between elements
                          }}
                        >
                          <Grid container spacing={2} alignItems="center">
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                width: "100%",
                              }}
                            >
                              <Box
                                sx={{ paddingLeft: "20px", paddingTop: "15px" }}
                              >
                                <Typography
                                  variant="h6"
                                  sx={{
                                    fontWeight: "600",
                                    fontSize: isXsOrSm ? "12px" : "15px",
                                    color: "rgba(75, 70, 92, 0.9)",
                                  }}
                                >
                                  {item?.docName}{" "}
                                  {item?.isOldValue && item?.isCheckNon!=true && (
                                    <>
                                      {" "}
                                      <span style={{ color: "red" }}>
                                        *
                                      </span>{" "}
                                    </>
                                  )}
                                </Typography>
                                <Typography
                                  sx={{
                                    fontWeight: "400",
                                    fontSize: "10px",
                                    color: "rgba(75, 70, 92, 0.8)",
                                  }}
                                >
                                  Upto 10 mb
                                </Typography>
                              </Box>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  paddingTop: "15px",
                                }}
                              >
                                {isMoveToNext === false && (
                                  <UploadFileInput
                                    handleUploadDocument={
                                      handleOpenUploadDocument
                                    }
                                    element={item}
                                    indexValue={index}
                                  />
                                )}
                              </Box>
                            </Box>
                          </Grid>
                        </Box>
                      </Grid>
                    )}
                  </>
                ))}
              </>
            )}
          </Grid>
        </div>
        {isMoveToNext === false && (
          <div style={{ paddingLeft: "16px", width: "161px" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                paddingTop: "20px",
              }}
              onClick={() => setIsAddNewDocModal(true)}
            >
              <AddIcon
                sx={{ fontSize: "16px", color: "#00B3B5", marginRight: "4px" }}
              />
              <Typography
                variant="h6"
                sx={{ fontWeight: "600", fontSize: "13px", color: "#00B3B5" }}
              >
                Add new document
              </Typography>
            </Box>
          </div>
        )}
      </>
    //);
  }, [formValue, documentId, documentIndex, isLoading, isXsOrSm]);
  const renderStepContent = React.useMemo(() => {
    return (
       <>
        <Divider sx={{ paddingTop: "20px" }} />
        <div className="padding-box">
          <Grid container item gap={1}>
            {formValue.some((item: any) => item.documentFiles.length > 0) && (
              <Grid item sm={12}>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: "600", fontSize: "13px", color: "#00B3B5" }}
                >
                  UPLOADED
                </Typography>
              </Grid>
            )}
          </Grid>
          <Grid container item spacing={2} sx={{ paddingTop: "10px" }}>
            {isLoading || isLoadingUpdate ? (
              <>
                {Array.from({ length: 4 }).map((_, index) => (
                  <Grid item xs={12} sm={12} md={6} key={index}>
                    <Box
                      sx={{
                        border: "1px solid #A8AAAE",
                        height: "60px",
                        borderRadius: "12px",
                        padding: "10px 16px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Box>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: "600",
                            fontSize: "15px",
                            color: "rgba(75, 70, 92, 0.9)",
                          }}
                        >
                          <Skeleton width={150} />
                        </Typography>
                        <Typography
                          sx={{
                            fontWeight: "400",
                            fontSize: "10px",
                            color: "rgba(75, 70, 92, 0.8)",
                          }}
                        >
                          <Skeleton width={100} />
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <IconButton bgColor="rgb(179 224 224)">
                          <Skeleton variant="circular" width={24} height={24} />
                        </IconButton>
                        <IconButton bgColor="rgb(179 224 224)">
                          <Skeleton variant="circular" width={24} height={24} />
                        </IconButton>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </>
            ) : (
              <>
                {formValue.map((item: any, index: any) => (
                  <>
                    {item?.documentFiles?.length > 0 && (
                      <Grid item xs={12} sm={12} md={6}>
                        <Box
                          sx={{
                            border: "1px solid #A8AAAE",
                            height: "60px",
                            borderRadius: "12px",
                            padding: "10px 16px",
                            display: "flex", // Use flex for alignment
                            alignItems: "center", // Align items vertically center
                            justifyContent: "space-between", // Add spacing between elements
                          }}
                        >
                          <Box>
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: "600",
                                fontSize: isXsOrSm ? "12px" : "15px",
                                color: "rgba(75, 70, 92, 0.9)",
                              }}
                            >
                              {item?.docName}
                              {item?.isOldValue &&item?.isCheckNon!=true && (
                                <>
                                  {" "}
                                  <span style={{ color: "red" }}>*</span>{" "}
                                </>
                              )}
                              {item?.docOtherName
                                ? `, ${item?.docOtherName}`
                                : item?.otherDocName
                                  ? `, ${item?.otherDocName}`
                                  : ""}
                            </Typography>
                            <Typography
                              sx={{
                                fontWeight: "400",
                                fontSize: "10px",
                                color: "rgba(75, 70, 92, 0.8)",
                              }}
                            >
                              Upto 10 mb
                            </Typography>
                          </Box>
                          <Box gap={5} sx={{ paddingTop: "5px",minWidth:"82px" }}>
                            {isXsOrSm ? (
                              <>
                                <VisibilityOutlinedIcon
                                  sx={{ color: "rgba(0, 179, 181, 0.8)" }}
                                  onClick={() =>
                                    handleOpenUploadDocument(item?.docId, index)
                                  }
                                />
                              </>
                            ) : (
                              <>
                                <IconButton
                                  bgColor="rgb(179 224 224)"
                                  onClick={() =>
                                    handleOpenUploadDocument(item?.docId, index)
                                  }
                                >
                                  {" "}
                                  <VisibilityOutlinedIcon
                                    sx={{ color: "rgba(0, 179, 181, 0.8)" }}
                                  />{" "}
                                </IconButton>
                              </>
                            )}

                            {isMoveToNext === false && (
                              <>
                                {item?.uploadedBy ==
                                  "customer" &&
                                  (
                                    <>
                                      {isXsOrSm ? (
                                        <>
                                          {" "}
                                          <DeleteOutlineOutlinedIcon
                                            sx={{ color: "#EA5455" }}
                                            onClick={() =>
                                              handleDeleteField(index, "ALL")
                                            }
                                          />{" "}
                                        </>
                                      ) : (
                                        <>
                                          <IconButton
                                            isTransparent={true}
                                            onClick={() =>
                                              handleDeleteField(index, "ALL")
                                            }
                                          >
                                            {" "}
                                            <DeleteOutlineOutlinedIcon
                                              sx={{ color: "#EA5455" }}
                                            />{" "}
                                          </IconButton>
                                        </>
                                      )}
                                    </>
                                  )}
                              </>
                            )}
                          </Box>
                        </Box>
                      </Grid>
                    )}
                  </>
                ))}
              </>
            )}
          </Grid>
        </div>

        <div className="padding-box" style={{ paddingTop: "0px" }}>
          <Grid container item sx={{ paddingTop: "20px" }}>
            {formValue.some((item: any) => item.documentFiles.length <= 0) && (
              <Grid item xs={12} sm={12} md={6}>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: "600", fontSize: "13px", color: "#00B3B5" }}
                >
                  TO BE UPLOADED
                </Typography>
              </Grid>
            )}
          </Grid>
          <Grid container item spacing={2} sx={{ paddingTop: "10px" }}>
            {isLoading || isLoadingUpdate ? (
              <>
                {Array.from({ length: 4 }).map((_, index) => (
                  <Grid item xs={12} sm={12} md={6} key={index}>
                    <Box
                      sx={{
                        border: "1px solid #A8AAAE",
                        height: "60px",
                        borderRadius: "12px",
                        padding: "10px 16px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Box>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: "600",
                            fontSize: "15px",
                            color: "rgba(75, 70, 92, 0.9)",
                          }}
                        >
                          <Skeleton width={150} />
                        </Typography>
                        <Typography
                          sx={{
                            fontWeight: "400",
                            fontSize: "10px",
                            color: "rgba(75, 70, 92, 0.8)",
                          }}
                        >
                          <Skeleton width={100} />
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <IconButton bgColor="rgb(179 224 224)">
                          <Skeleton variant="circular" width={24} height={24} />
                        </IconButton>
                        <IconButton bgColor="rgb(179 224 224)">
                          <Skeleton variant="circular" width={24} height={24} />
                        </IconButton>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </>
            ) : (
              <>
                {formValue.map((item: any, index: number) => (
                  <>
                    {item?.documentFiles?.length == 0 && (
                      <Grid item xs={12} sm={12} md={6}>
                        <Box
                          sx={{
                            border: "1px solid #A8AAAE",
                            height: "60px",
                            borderRadius: "12px",
                            padding: "10px 16px",
                            display: "flex", // Use flex for alignment
                            alignItems: "center", // Align items vertically center
                            justifyContent: "space-between", // Add spacing between elements
                          }}
                        >
                          <Grid container spacing={2} alignItems="center">
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                width: "100%",
                              }}
                            >
                              <Box
                                sx={{ paddingLeft: "20px", paddingTop: "15px" }}
                              >
                                <Typography
                                  variant="h6"
                                  sx={{
                                    fontWeight: "600",
                                    fontSize: isXsOrSm ? "12px" : "15px",
                                    color: "rgba(75, 70, 92, 0.9)",
                                  }}
                                >
                                  {item?.docName}{" "}
                                  {item?.isOldValue && item?.isCheckNon!=true &&(
                                    <>
                                      {" "}
                                      <span style={{ color: "red" }}>
                                        *
                                      </span>{" "}
                                    </>
                                  )}
                                </Typography>
                                <Typography
                                  sx={{
                                    fontWeight: "400",
                                    fontSize: "10px",
                                    color: "rgba(75, 70, 92, 0.8)",
                                  }}
                                >
                                  Upto 10 mb
                                </Typography>
                              </Box>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  paddingTop: "15px",
                                }}
                              >
                                {isMoveToNext === false && (
                                  <UploadFileInput
                                    handleUploadDocument={
                                      handleOpenUploadDocument
                                    }
                                    element={item}
                                    indexValue={index}
                                  />
                                )}
                              </Box>
                            </Box>
                          </Grid>
                        </Box>
                      </Grid>
                    )}
                  </>
                ))}
              </>
            )}
          </Grid>
        </div>
        {isMoveToNext === false && (
          <div style={{ paddingLeft: isXsOrSm ? "0px" : "16px", width: "100%" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                paddingTop: "20px",
              }}
              onClick={() => setIsAddNewDocModal(true)}
            >
              <AddIcon
                sx={{ fontSize: "16px", color: "#00B3B5", marginRight: "4px" }}
              />
              <Typography
                variant="h6"
                sx={{ fontWeight: "600", fontSize: "13px", color: "#00B3B5" }}
              >
                Add new document
              </Typography>
            </Box>
          </div>
        )}
      </>
    );
  }, [formValue, documentId, documentIndex, isLoading, isXsOrSm]);
  // const renderStepContent = () => {
  //   return <div>{handleShowUploadCard}</div>;
  // };

   
  const handleAddNewDocName = (event: any) => {
    const inputValue = event.target.value;
    const copyFormValue: any = [...formValue];
    copyFormValue[copyFormValue.length - 1].otherDocName = inputValue;
    setFormValue(copyFormValue);
    setOtherDocumentName(inputValue); // Track the input value for validation
  };

  // Validation function to check if upload button should be enabled
  const isUploadButtonEnabled = () => {
    // Find the selected document from nonMandatoryList
    const selectedDocument = nonMandatoryList.find(
      (elem) => elem?.value == newDocumentValue
    );
    
    // If it's an "Other" document type, check if otherDocumentName is filled
    if (selectedDocument && /others|other/i.test(selectedDocument?.label)) {
      return otherDocumentName.trim().length > 0;
    }
    
    // For non-Other documents, always enable if a document is selected
    return newDocumentValue !== 0;
  };
  const handleShowNewOtherField = React.useMemo(() => {
    const filterData: any = nonMandatoryList.findIndex(
      (elem: any) =>
        elem?.value == newDocumentValue && /others|other/i.test(elem?.label)
    );

    if (filterData != -1) {
      return (
        <Grid item xs={12} sm={12} md={12}>
          <div style={{ marginBottom: "2px" }}>
            <label
              htmlFor="custom-autocomplete"
              style={{
                fontSize: "13px",
                fontWeight: "400",
                color: "rgba(75, 70, 92, 0.8)",
                lineHeight: "18px",
              }}
            >
              {"Other Document Name"} <span style={{ color: "red" }}>*</span>
            </label>
          </div>
          <InputField
            InputLabelProps={{
              shrink: true,
            }}
            placeholder="Enter Other Document Name"
            variant="outlined"
            fullWidth
            rows={1}
            onChange={(e: any) => handleAddNewDocName(e)}
          />
        </Grid>
      );
    }
  }, [nonMandatoryList, newDocumentValue, formValue]);

  /**
   * Handles the deletion of a selected document from the form value.
   *
   * This function creates a copy of the current form value, filters out the document
   * with the specified `docId`, and updates the form value state with the filtered result.
   *
   * @returns {void}
   */
  const handleDeleteSelectedDocument = () => {
    const copyFormValue = [...formValue];
    const copySelectedDocuments = [...selectedDocuments];
    
    // Remove the document from formValue
    const updatedFormValue = copyFormValue.filter(
      (elem: any) => elem.docId !== newDocumentValue
    );
    
    // Remove the document from selectedDocuments
    const updatedSelectedDocuments = copySelectedDocuments.filter(
      (docId: any) => docId !== String(newDocumentValue)
    );
    
    setFormValue(updatedFormValue);
    setSelectedDocuments(updatedSelectedDocuments);
    setNewDocumentValue(0);
    setOtherDocumentName(""); // Reset other document name when document is deleted
  };
  return (
    <>
      {renderStepContent}
      <Toast
        open={toastOpen}
        message={message}
        severity={severity}
        onClose={handleToastClose}
      />

      <Modal
        open={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setDocIndex(0);
        }}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: isXsOrSm ? "300px" : "80%",
            height: "90%",
            minHeight: isXsOrSm ? "380px" : "90%",

            // marginLeft: isXsOrSm ? "32px" : "0px",
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
          }}
        >
          <CloseIcon
            onClick={() => {
              setIsModalOpen(false);
            }}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
            }}
          />
          {handleShowImageMList()}
        </Box>
      </Modal>

      {/* ================ Add New Document ================== */}

      <ModalPopup
        onNavigate={() => { }}
        isOpen={isAddNewDocModal}
        onClose={() => {
          setIsAddNewDocModal(false);
          setOtherDocumentName(""); // Reset other document name when modal closes
          handleDeleteSelectedDocument();
        }}
        title=""
        style={{ minWidth: "330px", height: "300px" }}
      >
        <Box
          sx={{
            height: "180px", // Adjust this value to leave space for header/foote
          }}
        >
          {/* Modal Header */}
          <Typography
            variant="h6"
            fontWeight="bold"
            sx={{ mb: 2, color: "rgba(75, 70, 92, 0.9)" }}
          >
            Upload New Document
          </Typography>

          <Box>
            <Grid item sm={12}>
              <SelectOptions
                options={nonMandatoryList}
                label={`Document Name`}
                value={newDocumentValue}
                isMandatory={false}
                onChange={(value: string | number) => {
                  handleSelectedNewFieldValue(value);
                }}
              />
            </Grid>

            {handleShowNewOtherField}
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            position: "sticky",
            background: "white", // Match modal background
            zIndex: 10, // Ensure it appears above the scrollable area
            p: 1,
          }}
        >
          {isUploadButtonEnabled() ? (
            <Typography
              variant="h6"
              sx={{
                fontWeight: "600",
                fontSize: "13px",
                color: "#00B3B5",
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
              }}
              onClick={() => {
                setIsDocumentData(false);
                setDocumentId(newDocumentValue);
                setDocumentIndex(formValue.length - 1);
                setIsUploadModal(true);
              }}
            >
              <AddIcon /> &nbsp; Upload Document
            </Typography>
          ) : (
            <Typography
              variant="h6"
              sx={{
                fontWeight: "600",
                fontSize: "13px",
                color: "#CCCCCC", // Disabled color
                display: "flex",
                alignItems: "center",
                cursor: "not-allowed",
                opacity: 0.6,
              }}
            >
              <AddIcon /> &nbsp; Upload Document
            </Typography>
          )}
        </Box>
      </ModalPopup>

      {/* ==================================================== */}

      {/* ================ Upload Document Modal ============== */}
      <ModalPopup
        onNavigate={() => { }}
        isOpen={isUploadModal}
        onClose={() => {
          setDocumentId(0);
          setDocumentIndex(0);
          setIsUploadModal(false);
        }}
        title=""
        style={{
          minWidth: "333px",
          minHeight: "380px",
          marginLeft: isXsOrSm ? "32px" : "0px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%", // Ensure the modal takes full height
          }}
        >
          {/* Modal Header */}
          <Typography
            variant="h6"
            fontWeight="bold"
            sx={{ mb: 2, color: "rgba(75, 70, 92, 0.9)" }}
          >
            Upload Document
          </Typography>

          {/* Scrollable List Area */}
          <Box
            sx={{
              flex: 1, // Makes the listing area take available space
              overflowY: "auto", // Enables vertical scrolling
              maxHeight: "calc(100vh - 200px)", // Adjust this value to leave space for header/foote
            }}
          >
            {isUploadLoading ? (
              <>
                {Array.from({ length: 3 }).map((_, index) => (
                  <Box
                    sx={{
                      border: "1px solid #A8AAAE",
                      height: "60px",
                      borderRadius: "12px",
                      padding: "10px 16px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 2, // Add margin between items
                    }}
                    key={index}
                  >
                    <Box>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: "600",
                          fontSize: "15px",
                          color: "rgba(75, 70, 92, 0.9)",
                        }}
                      >
                        <Skeleton width={120} />
                      </Typography>
                      <Typography
                        sx={{
                          fontWeight: "400",
                          fontSize: "10px",
                          color: "rgba(75, 70, 92, 0.8)",
                        }}
                      >
                        <Skeleton width={80} />
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", gap: 2 }}>
                      <IconButton bgColor="rgb(179 224 224)">
                        <Skeleton variant="circular" width={24} height={24} />
                      </IconButton>
                      <IconButton bgColor="rgb(179 224 224)">
                        <Skeleton variant="circular" width={24} height={24} />
                      </IconButton>
                    </Box>
                  </Box>
                ))}
              </>
            ) : (
              <>
                {isDocumentData ? (
                  <>
                    {formValue?.[documentIndex]?.documentFiles?.map(
                      (elem: any, index: any) => (
                        <>
                          <Box
                            sx={{
                              border: "1px solid #A8AAAE",
                              height: "60px",
                              borderRadius: "12px",
                              padding: "10px 16px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              mb: 2, // Add margin between items
                            }}
                            key={index}
                          >
                            <Box>
                              <Typography
                                variant="h6"
                                sx={{
                                  fontWeight: "600",
                                  fontSize: isXsOrSm ? "12px" : "15px",
                                  color: "rgba(75, 70, 92, 0.9)",
                                }}
                              >
                                {elem?.docName?.split("/")?.[1]}
                              </Typography>
                              <Typography
                                sx={{
                                  fontWeight: "400",
                                  fontSize: "10px",
                                  color: "rgba(75, 70, 92, 0.8)",
                                }}
                              >
                                Upto 10 mb
                              </Typography>
                            </Box>
                            <Box sx={{ display: "flex", gap: 1 }}>
                              {isXsOrSm ? (
                                <>
                                  <VisibilityOutlinedIcon
                                    sx={{ color: "rgba(0, 179, 181, 0.8)" }}
                                    onClick={() => {
                                      setIsModalOpen(true);
                                      setDocIndex(index);
                                      handleGetDocsFileList(elem?.docId);
                                    }}
                                  />
                                </>
                              ) : (
                                <>
                                  <IconButton
                                    bgColor="rgb(179 224 224)"
                                    onClick={() => {
                                      setIsModalOpen(true);
                                      setDocIndex(index);
                                      handleGetDocsFileList(elem?.docId);
                                    }}
                                  >
                                    <VisibilityOutlinedIcon
                                      sx={{ color: "rgba(0, 179, 181, 0.8)" }}
                                    />
                                  </IconButton>
                                </>
                              )}

                              {isMoveToNext === false && (
                                <>
                                  {elem?.uploadedBy ==
                                    "customer" &&
                                    (
                                      <>
                                        {isXsOrSm ? (
                                          <>
                                            <DeleteOutlineOutlinedIcon
                                              sx={{ color: "#EA5455" }}
                                              onClick={() =>
                                                handleDeleteField(
                                                  index,
                                                  "SINGLE"
                                                )
                                              }
                                            />
                                          </>
                                        ) : (
                                          <>
                                            <IconButton
                                              isTransparent={true}
                                              onClick={() =>
                                                handleDeleteField(
                                                  index,
                                                  "SINGLE"
                                                )
                                              }
                                            >
                                              <DeleteOutlineOutlinedIcon
                                                sx={{ color: "#EA5455" }}
                                              />
                                            </IconButton>
                                          </>
                                        )}
                                      </>
                                    )}
                                </>
                              )}
                            </Box>
                          </Box>
                        </>
                      )
                    )}
                  </>
                ) : (
                  <>
                    <input
                      type="file"
                      accept=".jpeg,.png,.pdf"
                      style={{ display: "none" }}
                      id="file-upload"
                      onChange={(e) =>
                        handleFileChange(
                          documentId,
                          e.target.files,
                          documentIndex
                        )
                      }
                    />
                    <label htmlFor="file-upload">
                      <Box
                        sx={{
                          border: "2px dashed #C4C4C4",
                          borderRadius: "8px",
                          textAlign: "center",
                          padding: "20px",
                          cursor: "pointer",
                          height: "238px",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <label htmlFor="file-upload">
                          <IconButton
                            component="span"
                            size="medium"
                            bgColor="rgb(179 224 224)"
                            sx={{ color: "rgba(75, 70, 92, 0.8)" }}
                          >
                            <UploadIcon fontSize="medium" />
                          </IconButton>
                        </label>
                        <Typography
                          variant="body1"
                          fontWeight="600"
                          sx={{ mt: 1, color: "#424242" }}
                        >
                          Click to upload
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ mt: 1, color: "#9E9E9E" }}
                        >
                          File Format: .jpeg, .png & File Size (pdf): Up to 10
                          MB
                        </Typography>
                      </Box>
                    </label>
                  </>
                )}
              </>
            )}
          </Box>

          {/* Add More Button */}
          {isDocumentData && isMoveToNext === false && (
            <Box
              sx={{
                display: "flex",
                position: "sticky",
                bottom: 14, // Leave space for the "Save" button
                background: "white", // Match modal background
                zIndex: 10, // Ensure it appears above the scrollable area
                p: 1,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: "600",
                  fontSize: "13px",
                  color: "#00B3B5",
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                }}
                onClick={() => setIsDocumentData(false)}
              >
                <AddIcon /> &nbsp; Add More
              </Typography>
            </Box>
          )}
        </Box>
      </ModalPopup>
      {/* =================================================== */}
    </>
  );
};

export default AddNewDocuments;